/**
 * Pieces the Loans screens share.
 */

import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  LOAN_CATEGORIES,
  LOAN_CATEGORY_LABELS,
  LOAN_STATUS_LABELS,
  type LoanCategory,
  type LoanSort,
  type LoanStatus,
} from '@cenox/shared';
import { Badge, cn } from '../../components/ui';
import { date, money, moneyCompact } from '../../lib/format';
import { paletteAt } from '../reports/palette';
import { useModules } from '../../lib/modules';

/**
 * Whether this business has riders to lend to: deliveries or Remittance &
 * Hire Purchase. A pharmacy or a shop does not, so it is never offered rider
 * or vehicle loans, or a rider as a borrower. Decided by modules, like the
 * menu; while they are unknown everything is offered rather than guessed at.
 */
export function useHasRiders(): boolean {
  const { active, known, isLoading } = useModules();
  return !known || isLoading || active.has('delivery') || active.has('rider_finance');
}

/** The loan categories this business can use. Existing loans keep theirs whatever this says. */
export function useLoanCategories(): readonly LoanCategory[] {
  const riders = useHasRiders();
  return riders ? LOAN_CATEGORIES : LOAN_CATEGORIES.filter((c) => c !== 'rider' && c !== 'vehicle');
}

const STATUS_TONES: Record<LoanStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'neutral',
  active: 'info',
  overdue: 'danger',
  completed: 'success',
  cancelled: 'neutral',
  written_off: 'warning',
};

export function LoanStatusBadge({ status }: { status: LoanStatus }): ReactNode {
  return <Badge tone={STATUS_TONES[status]}>{LOAN_STATUS_LABELS[status]}</Badge>;
}

/**
 * A calendar date the API sent as YYYY-MM-DD.
 *
 * Already a day in the business's timezone, so it is formatted at UTC — the
 * moment `new Date('2026-11-01')` names — and never shifted into the
 * browser's own zone, which would show 31 October to anybody west of London.
 */
export function day(iso: string | null | undefined): string {
  return date(iso, 'UTC');
}

/** Category, without the trailing "loans" the tab labels carry. */
export function categoryName(category: LoanCategory): string {
  return LOAN_CATEGORY_LABELS[category].replace(/ loans$/, '');
}

/**
 * One of the four summary cards: the amount, and how many loans make it up.
 * The exact amount is on hover; the tile shows the compact one.
 */
export function FigureCard({
  label,
  amount,
  count,
  currency,
  hint,
  tone = 'plain',
  icon,
}: {
  label: string;
  amount: string | undefined;
  count: number | undefined;
  currency: string;
  hint: string;
  tone?: 'plain' | 'good' | 'owed' | 'warn';
  icon: ReactNode;
}): ReactNode {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-slate-600">{label}</p>
        <span
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-lg',
            tone === 'plain' && 'bg-brand-50 text-brand-700',
            tone === 'good' && 'bg-emerald-50 text-emerald-700',
            tone === 'warn' && 'bg-amber-50 text-amber-700',
            tone === 'owed' && 'bg-rose-50 text-rose-700',
          )}
          aria-hidden
        >
          {icon}
        </span>
      </div>
      <p
        className={cn(
          'mt-2 text-2xl font-semibold tabular-nums tracking-tight',
          tone === 'owed' ? 'text-rose-700' : 'text-slate-900',
        )}
        title={amount ? money(amount, currency) : undefined}
      >
        {amount === undefined ? '—' : moneyCompact(amount, currency)}
      </p>
      <p className="mt-1 text-xs text-slate-500">
        {count === undefined ? ' ' : `${count} ${count === 1 ? 'loan' : 'loans'}`} · {hint}
      </p>
    </div>
  );
}

export function SortHeader({
  label,
  by,
  sort,
  onSort,
  align = 'left',
}: {
  label: string;
  by: LoanSort;
  sort: { by: LoanSort; dir: 'asc' | 'desc' };
  onSort: (by: LoanSort) => void;
  align?: 'left' | 'right';
}): ReactNode {
  const active = sort.by === by;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn('px-2 py-2.5 font-medium', align === 'right' && 'text-right')}
    >
      <button
        type="button"
        onClick={() => onSort(by)}
        className={cn('inline-flex items-center gap-1 whitespace-nowrap uppercase tracking-wide hover:text-slate-900', active && 'text-slate-900')}
      >
        {label}
        <Icon className="size-3.5" aria-hidden />
      </button>
    </th>
  );
}

/** Outstanding principal by category, as a donut with its legend beside it. */
export function CategoryDonut({
  rows,
  currency,
}: {
  rows: Array<{ category: LoanCategory; amount: string; count: number }>;
  currency: string;
}): ReactNode {
  if (rows.length === 0) {
    return <p className="py-10 text-center text-sm text-slate-500">Nothing outstanding.</p>;
  }
  const total = rows.reduce((sum, r) => sum + Number(r.amount), 0);
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="h-40 w-40 shrink-0" role="img" aria-label="Outstanding principal by loan type">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows.map((r) => ({ name: categoryName(r.category), value: Number(r.amount) }))}
              dataKey="value"
              innerRadius="58%"
              outerRadius="88%"
              paddingAngle={2}
              stroke="#ffffff"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {rows.map((_, index) => (
                <Cell key={index} fill={paletteAt(index)} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: unknown) => money(Number(value).toFixed(2), currency)}
              contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="w-full min-w-0 flex-1 space-y-2">
        {rows.map((r, index) => (
          <li key={r.category} className="flex items-center gap-2 text-sm">
            <span className="size-2.5 shrink-0 rounded-full" style={{ background: paletteAt(index) }} aria-hidden />
            <span className="min-w-0 flex-1 truncate text-slate-700">{categoryName(r.category)}</span>
            <span className="tabular-nums text-slate-900">{money(r.amount, currency)}</span>
            <span className="w-11 text-right text-xs tabular-nums text-slate-500">
              {total > 0 ? `${Math.round((Number(r.amount) / total) * 100)}%` : '—'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
